import React, { useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Link as MuiLink,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  TablePagination,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  TableRowsRounded as TableViewIcon,
  GridViewRounded as GridViewIcon,
} from '@mui/icons-material';
import { MobileEntityCard, MobileCardAccent } from 'shared/components/MobileEntityCard';
import {
  TABLE_SPACING,
  TABLE_SPACING_VALUES,
  tableRowSx,
  tableBodyCellSx,
  paginationSx,
  actionMenuButtonSx,
  emptyStateSx,
} from 'shared/theme/tableStyles';

import type { Palette } from '@mui/material/styles';

/**
 * Custom palette consumed by DataTable to override the default MUI theme
 * (e.g. per-company branding). Compatible with MUI's `Palette` type.
 */
export type DataTablePalette = Palette;

/**
 * Generic column descriptor. `T` is the row shape — kept as `any` by
 * default so existing call sites work unchanged, but new code is
 * encouraged to pass the concrete row type for type safety.
 */
 
export interface ColumnDef<T = any> {
  id: string;
  label: string;
  width?: string;
  render?: (row: T, index: number) => React.ReactNode;
  linkTo?: (row: T) => string;
  /**
   * Marks the column header clickable to sort the table. Sort uses
   * `sortValue(row)` if defined; otherwise it falls back to `row[id]`.
   * The optional `statusOrder` lets you define custom precedence for
   * string-status columns (e.g. active before warning before closed).
   */
  sortable?: boolean;
  sortValue?: (row: T) => string | number | Date | null | undefined;
  statusOrder?: string[];
  align?: 'left' | 'center' | 'right';
  visible?: boolean;
  mobileVisible?: boolean;
  hideMobileLabel?: boolean;
}

 
export interface ActionItem<T = any> {
  id: string;
  label?: string;
  labelFn?: (row: T) => string;
  icon?: React.ReactNode;
  onClick: (row: T) => void;
  color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  divider?: boolean;
  hidden?: (row: T) => boolean; // Conditionally hide action for specific rows
  disabled?: (row: T) => boolean; // Conditionally disable action for specific rows
  disabledTooltip?: string | ((row: T) => string); // Explanation shown when disabled
}

/**
 * Opt-in config that renders the mobile "cards" variant as the shared
 * MobileEntityCard (colour-banded card) instead of the default label/value
 * grid. Provide per-row accessors; anything omitted is simply not rendered.
 * When `footerRight` is omitted the row's action menu (MoreVert) is used.
 */
export interface MobileCardConfig<T = any> {
  accent?: (row: T) => MobileCardAccent | undefined;
  headerLeft?: (row: T) => React.ReactNode;
  headerRight?: (row: T) => React.ReactNode;
  eyebrow?: (row: T) => React.ReactNode;
  title: (row: T) => React.ReactNode;
  status?: (row: T) => { label: React.ReactNode; color?: MobileCardAccent } | undefined;
  footerLeft?: (row: T) => React.ReactNode;
  footerRight?: (row: T) => React.ReactNode;
  onClick?: (row: T) => void;
}

 
export interface DataTableProps<T = any> {
  columns: ColumnDef<T>[];
  rows: T[];
  actions?: ActionItem<T>[];
  onRowClick?: (row: T, index: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  pagination?: {
    page: number;
    rowsPerPage: number;
    total: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
  };
  striped?: boolean;
  hover?: boolean;
  mobileVariant?: 'cards' | 'compact-list';
  mobileCard?: MobileCardConfig<T>;
  /**
   * Show a desktop Table/Grid view switch (top-left). Only offered when a
   * `mobileCard` mapping exists (grid view reuses it). On mobile the card
   * view is always used, so the toggle is hidden there.
   */
  viewToggle?: boolean;
  /** localStorage key to remember the chosen desktop view per table. */
  viewStorageKey?: string;
  rowSx?: (row: T) => Record<string, unknown>;
  customPalette?: DataTablePalette;
  /**
   * Initial sort applied on first render. Either pass a column id (defaults
   * to 'asc') or a tuple. After this, sort is user-controlled via clicks.
   * Pass `null` to start unsorted.
   */
  defaultSort?: string | { columnId: string; direction: 'asc' | 'desc' } | null;
  /**
   * Row ids (matched against `row._id ?? row.id`) that should float to the
   * top of the table ahead of everything else, regardless of the active
   * sort column/direction — e.g. a just-created row the caller wants the
   * user to spot immediately. Pinned rows keep their relative order from
   * `rows`; the remaining rows are sorted normally beneath them.
   */
  pinnedRowIds?: Iterable<string>;
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  actions = [],
  onRowClick,
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  pagination,
  striped = true,
  hover = true,
  mobileVariant = 'cards',
  mobileCard,
  viewToggle = false,
  viewStorageKey,
  rowSx,
  customPalette,
  defaultSort = null,
  pinnedRowIds,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Desktop Table/Grid view switch (opt-in via `viewToggle`). Remembered per
  // table in localStorage when `viewStorageKey` is given.
  const [desktopView, setDesktopView] = useState<'table' | 'grid'>(() => {
    if (!viewToggle || !viewStorageKey) return 'table';
    try {
      const saved = localStorage.getItem(`dt-view-${viewStorageKey}`);
      return saved === 'grid' ? 'grid' : 'table';
    } catch {
      return 'table';
    }
  });
  const handleDesktopViewChange = (
    _e: React.MouseEvent<HTMLElement>,
    next: 'table' | 'grid' | null,
  ) => {
    if (!next) return;
    setDesktopView(next);
    try {
      if (viewStorageKey) localStorage.setItem(`dt-view-${viewStorageKey}`, next);
    } catch {
      /* storage unavailable — keep in-memory only */
    }
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  // Sort state. Initial value from defaultSort prop; clicks on a sortable
  // column header cycle: asc → desc → asc. Different column resets to asc.
  const initialSort = useMemo(() => {
    if (!defaultSort) return { columnId: null as string | null, direction: 'asc' as 'asc' | 'desc' };
    if (typeof defaultSort === 'string') return { columnId: defaultSort, direction: 'asc' as const };
    return { columnId: defaultSort.columnId, direction: defaultSort.direction };
  }, [defaultSort]);
  const [sortColumnId, setSortColumnId] = useState<string | null>(initialSort.columnId);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSort.direction);

  const handleSortClick = (columnId: string) => {
    if (sortColumnId === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumnId(columnId);
      setSortDirection('asc');
    }
  };

  const pinnedIdSet = useMemo(
    () => (pinnedRowIds ? new Set(pinnedRowIds) : null),
    [pinnedRowIds],
  );

  const applySort = useCallback((list: any[]): any[] => {
    if (!sortColumnId) return list;
    const col = columns.find((c) => c.id === sortColumnId);
    if (!col || !col.sortable) return list;

    const getValue = (row: any): any => {
      if (col.sortValue) return col.sortValue(row);
      return row[col.id];
    };

    const sorted = [...list].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      // Custom status order takes precedence
      if (col.statusOrder && typeof av === 'string' && typeof bv === 'string') {
        const ai = col.statusOrder.indexOf(av);
        const bi = col.statusOrder.indexOf(bv);
        // unknown statuses go to the end
        const ax = ai === -1 ? col.statusOrder.length : ai;
        const bx = bi === -1 ? col.statusOrder.length : bi;
        return ax - bx;
      }

      // Null/undefined always go last
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (av instanceof Date && bv instanceof Date) {
        return av.getTime() - bv.getTime();
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return av - bv;
      }
      // Default: locale-aware string compare (handles tildes, ñ, case)
      return String(av).localeCompare(String(bv), 'es', { sensitivity: 'base', numeric: true });
    });

    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [sortColumnId, sortDirection, columns]);

  // Pinned rows (e.g. a just-added user) always lead the table, in their
  // `rows`-array order, untouched by the active sort — only the remainder
  // gets sorted beneath them.
  const sortedRows = useMemo(() => {
    if (!pinnedIdSet || pinnedIdSet.size === 0) return applySort(rows);
    const pinned: any[] = [];
    const rest: any[] = [];
    rows.forEach((row) => {
      const id = row?._id ?? row?.id;
      (id != null && pinnedIdSet.has(id) ? pinned : rest).push(row);
    });
    if (pinned.length === 0) return applySort(rows);
    return [...pinned, ...applySort(rest)];
  }, [rows, pinnedIdSet, applySort]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowIndex: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(rowIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  const handleActionClick = (action: ActionItem, row: any) => {
    action.onClick(row);
    handleMenuClose();
  };

  const getActionLabel = (action: ActionItem, row: any) => {
    if (action.labelFn) {
      return action.labelFn(row);
    }
    return action.label || '';
  };

  // Renders a single action as a MenuItem. When the action is disabled for
  // this row, the item is greyed out and (if a `disabledTooltip` is given)
  // wrapped in a Tooltip so the user understands why — the span wrapper is
  // required because a disabled MenuItem swallows pointer events.
  const renderActionMenuItem = (action: ActionItem, row: any) => {
    const isDisabled = action.disabled?.(row) ?? false;
    const tooltip = typeof action.disabledTooltip === 'function'
      ? action.disabledTooltip(row)
      : action.disabledTooltip;
    const item = (
      <MenuItem
        key={action.id}
        disabled={isDisabled}
        onClick={() => handleActionClick(action, row)}
        sx={{
          color: action.color === 'error' ? 'error.main' : 'inherit',
          borderBottom: action.divider ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        {action.icon && (
          <Box sx={{ mr: TABLE_SPACING.GAP_SMALL, display: 'flex', alignItems: 'center' }}>
            {action.icon}
          </Box>
        )}
        {getActionLabel(action, row)}
      </MenuItem>
    );
    if (isDisabled && tooltip) {
      return (
        <Tooltip key={action.id} title={tooltip} arrow placement="left">
          <span style={{ display: 'block' }}>{item}</span>
        </Tooltip>
      );
    }
    return item;
  };

  const visibleColumns = columns.filter(col =>
    col.visible !== false && (!isMobile || col.mobileVisible !== false)
  );

  // Grid/card layout — shared by the mobile card view and the opt-in desktop
  // grid view. `desktopGrid` lays cards out in a responsive multi-column grid;
  // otherwise a single column (phones).
  const renderCards = (desktopGrid: boolean) => (
    <Box>
      <Box
        sx={
          desktopGrid
            ? {
                display: 'grid',
                gridTemplateColumns: { md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                gap: TABLE_SPACING.GAP_MEDIUM,
                alignItems: 'start',
              }
            : { display: 'flex', flexDirection: 'column', gap: TABLE_SPACING.GAP_MEDIUM }
        }
      >
        {mobileCard && sortedRows.map((row, rowIndex) => {
          const hasRowActions = actions.length > 0 && actions.filter(a => !a.hidden?.(row)).length > 0;
          const footerRight = mobileCard.footerRight
            ? mobileCard.footerRight(row)
            : hasRowActions
              ? (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, rowIndex); }}
                  sx={actionMenuButtonSx}
                  aria-label="Acciones"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )
              : undefined;
          return (
            <MobileEntityCard
              key={row?._id ?? row?.id ?? rowIndex}
              accent={mobileCard.accent?.(row)}
              headerLeft={mobileCard.headerLeft?.(row)}
              headerRight={mobileCard.headerRight?.(row)}
              eyebrow={mobileCard.eyebrow?.(row)}
              title={mobileCard.title(row)}
              status={mobileCard.status?.(row)}
              footerLeft={mobileCard.footerLeft?.(row)}
              footerRight={footerRight}
              onClick={
                mobileCard.onClick
                  ? () => mobileCard.onClick!(row)
                  : onRowClick
                    ? () => onRowClick(row, rowIndex)
                    : undefined
              }
            />
          );
        })}
        {!mobileCard && sortedRows.map((row, rowIndex) => (
          <Card
            key={row?._id ?? row?.id ?? rowIndex}
            sx={{
              position: 'relative',
              ...(rowSx ? rowSx(row) : {}),
            }}
          >
            <CardContent sx={{ p: TABLE_SPACING.CELL_PADDING, '&:last-child': { pb: TABLE_SPACING.CELL_PADDING } }}>
              <Grid container spacing={TABLE_SPACING_VALUES.GAP_SMALL / 8}>
                {visibleColumns.map((column) => {
                  const value = row[column.id];
                  const linkTo = column.linkTo?.(row);

                  return (
                    <Grid size={{ xs: 12, sm: 6 }} key={column.id}>
                      {!column.hideMobileLabel && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {column.label}
                        </Typography>
                      )}
                      {column.render ? (
                        column.render(row, rowIndex)
                      ) : linkTo ? (
                        <MuiLink
                          href={linkTo}
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            color: (theme) => theme.palette.background.primary,
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {value}
                        </MuiLink>
                      ) : (
                        <Typography variant="body2" fontWeight={500}>
                          {value}
                        </Typography>
                      )}
                    </Grid>
                  );
                })}
              </Grid>

              {actions.length > 0 && actions.filter(action => !action.hidden?.(row)).length > 0 && (
                <>
                  <Divider sx={{ my: TABLE_SPACING.GAP_SMALL }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: TABLE_SPACING.GAP_SMALL }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, rowIndex)}
                      sx={actionMenuButtonSx}
                      aria-label="Acciones"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Single shared action menu for the card layout */}
      {actions.length > 0 && selectedRowIndex !== null && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actions
            .filter(action => !action.hidden?.(sortedRows[selectedRowIndex]))
            .map((action) => renderActionMenuItem(action, sortedRows[selectedRowIndex]))}
        </Menu>
      )}

      {pagination && (
        <Paper sx={{ mt: TABLE_SPACING.GAP_MEDIUM }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
            onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={paginationSx}
          />
        </Paper>
      )}
    </Box>
  );

  // Desktop-only Table/Grid switch. Only meaningful when a card mapping exists.
  const showViewToggle = viewToggle && !!mobileCard && !isMobile;
  const viewToggleBar = showViewToggle ? (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: TABLE_SPACING.GAP_SMALL }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={desktopView}
        onChange={handleDesktopViewChange}
        aria-label="Vista de tabla o cuadrícula"
      >
        <ToggleButton value="table" aria-label="Vista de tabla">
          <Tooltip title="Tabla"><TableViewIcon fontSize="small" /></Tooltip>
        </ToggleButton>
        <ToggleButton value="grid" aria-label="Vista de cuadrícula">
          <Tooltip title="Cuadrícula"><GridViewIcon fontSize="small" /></Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  ) : null;

  if (rows.length === 0 && !loading) {
    return (
      <Box sx={emptyStateSx}>
        <Typography color="textSecondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  if (!isMobile) {
    // Desktop grid view (opt-in). The view switch sits above either layout.
    if (showViewToggle && desktopView === 'grid') {
      return (
        <Box>
          {viewToggleBar}
          {renderCards(true)}
        </Box>
      );
    }
    return (
      <Box>
        {viewToggleBar}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 1,
          borderRadius: 3,
          // Let wide tables scroll horizontally instead of clipping/squishing
          // their columns. Vertical stays hidden so the rounded corners hold.
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{
              bgcolor: (themeParam) => themeParam.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
            }}>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sortDirection={sortColumnId === column.id ? sortDirection : false}
                  sx={{
                    width: column.width,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortColumnId === column.id}
                      direction={sortColumnId === column.id ? sortDirection : 'asc'}
                      onClick={() => handleSortClick(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell
                  align="center"
                  sx={{
                    width: '50px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, rowIndex) => (
              <TableRow
                key={row?._id ?? row?.id ?? rowIndex}
                hover={hover}
                onClick={() => onRowClick?.(row, rowIndex)}
                sx={{
                  ...tableRowSx,
                  cursor: onRowClick ? 'pointer' : 'default',
                  bgcolor: striped && rowIndex % 2 === 0
                    ? (themeParam) => themeParam.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50'
                    : 'inherit',
                  // Spread last so a caller's rowSx (e.g. a highlight tint)
                  // can override the striping bgcolor above — previously
                  // this was spread before bgcolor and got silently clobbered
                  // on every even row.
                  ...(rowSx ? rowSx(row) : {}),
                }}
              >
                {visibleColumns.map((column) => {
                  const value = row[column.id];
                  const linkTo = column.linkTo?.(row);

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      sx={{
                        ...tableBodyCellSx,
                        // A hardcoded 'default' here would override the
                        // row's own cursor (set below from onRowClick), so
                        // cells with no per-column link just inherit it —
                        // only a genuine cell-level link forces 'pointer'.
                        cursor: linkTo ? 'pointer' : 'inherit',
                      }}
                    >
                      {column.render ? (
                        column.render(row, rowIndex)
                      ) : linkTo ? (
                        <MuiLink
                          href={linkTo}
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            color: (theme) => theme.palette.background.primary,
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {value}
                        </MuiLink>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
                {actions.length > 0 && (
                  <TableCell align="center" sx={tableBodyCellSx}>
                    {actions.filter(action => !action.hidden?.(row)).length > 0 && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, rowIndex)}
                        sx={actionMenuButtonSx}
                        aria-label="Acciones"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Single shared action menu for all rows */}
        {actions.length > 0 && selectedRowIndex !== null && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {actions
              .filter(action => !action.hidden?.(sortedRows[selectedRowIndex]))
              .map((action) => renderActionMenuItem(action, sortedRows[selectedRowIndex]))}
          </Menu>
        )}

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
            onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            sx={paginationSx}
          />
        )}
      </TableContainer>
      </Box>
    );
  }

  if (mobileVariant === 'cards') {
    return renderCards(false);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: TABLE_SPACING.GAP_SMALL }}>
      {sortedRows.map((row, rowIndex) => {
        const primaryCol = visibleColumns[0];
        const primaryValue = primaryCol.render
          ? primaryCol.render(row, rowIndex)
          : row[primaryCol.id];

        return (
          <Paper
            key={row?._id ?? row?.id ?? rowIndex}
            sx={{
              p: TABLE_SPACING.CELL_PADDING,
              ...(rowSx ? rowSx(row) : {}),
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: TABLE_SPACING.GAP_MEDIUM }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {primaryValue}
                </Typography>
                {visibleColumns.slice(1, 2).map((col) => (
                  <Typography key={col.id} variant="caption" color="text.secondary">
                    {col.render ? col.render(row, rowIndex) : row[col.id]}
                  </Typography>
                ))}
              </Box>
              {actions.length > 0 && actions.filter(action => !action.hidden?.(row)).length > 0 && (
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, rowIndex)}
                  sx={actionMenuButtonSx}
                  aria-label="Acciones"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Paper>
        );
      })}

      {/* Single shared action menu for compact list */}
      {actions.length > 0 && selectedRowIndex !== null && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {actions
            .filter(action => !action.hidden?.(sortedRows[selectedRowIndex]))
            .map((action) => renderActionMenuItem(action, sortedRows[selectedRowIndex]))}
        </Menu>
      )}

      {pagination && (
        <Paper sx={{ mt: TABLE_SPACING.GAP_MEDIUM }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
            onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
            sx={paginationSx}
          />
        </Paper>
      )}
    </Box>
  );
};

export default DataTable;